import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Preloader_002 — "Words" preloader
 * ---------------------------------
 * Sequence:
 *   1. First word fades in.
 *   2. Every following word hard-snaps in — no easing, no motion, an
 *      instant cut — each held on screen just long enough to register.
 *   3. After the last word, the whole coloured panel first rises 4vh as
 *      a flat, uncurved lift (the screen stays fully covered — this is
 *      just a wind-up), then continues rising with its bottom edge now
 *      a shallow curve (dips down at the centre, tapers up at the
 *      corners) sweeping upward, revealing the app underneath.
 *
 * Usage — identical shape to the original demo:
 *   const [showPreloader, setShowPreloader] = useState(true);
 *   <AnimatePresence mode="wait">
 *     {showPreloader && (
 *       <Preloader_002 onComplete={() => setShowPreloader(false)} />
 *     )}
 *   </AnimatePresence>
 *
 * `onComplete` fires once the wipe has fully cleared the screen — wire it
 * to flipping `showPreloader` off, or to whatever real "assets are ready"
 * check you're gating on.
 */

export interface Preloader002Props {
  /** Words/phrases cycled through while assets finish loading. */
  words?: string[];
  /** How long the first word holds after its fade-in, in ms. */
  firstWordDuration?: number;
  /** How long every subsequent word holds before snapping to the next, in ms. */
  wordDuration?: number;
  /** How long the curved wipe-up takes, in ms (includes the initial flat rise). */
  wipeDuration?: number;
  /** How far the panel rises, in vh, before the curve reveal begins. */
  curveMarginVh?: number;
  /** How deep the curve dips at its centre once revealing, in vh. */
  curveDepthVh?: number;
  /**
   * Gates the wipe. Defaults to true (wipe fires as soon as the last word's
   * hold time elapses, i.e. purely time-driven). Pass `false` while real
   * assets (e.g. the .glb models) are still loading — the preloader will
   * hold on the last word indefinitely and only start the wipe the instant
   * this flips to `true`, so the reveal never happens before the app
   * underneath is actually ready to be seen.
   */
  ready?: boolean;
  /** Panel colour — should match (or intentionally contrast with) the app's own background. */
  backgroundColor?: string;
  /** Text colour. */
  textColor?: string;
  /** Called once the wipe has fully left the screen. */
  onComplete?: () => void;
}

const DEFAULT_WORDS = [
  "Home Services",
  "Fashion & Beauty",
  "Furniture & Home Goods",
  "Food & Drinks",
  "Tech & Electronics",
  "Academic Services",
  "Events & Creative",
];

export default function Preloader_002({
  words = DEFAULT_WORDS,
  firstWordDuration = 700,
  wordDuration = 300,
  wipeDuration = 900,
  curveMarginVh = 4,
  curveDepthVh = 8,
  ready = true,
  backgroundColor = "#1006C5",
  textColor = "#FFFFFF",
  onComplete,
}: Preloader002Props) {
  const [index, setIndex] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [wiping, setWiping] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const isLast = index === words.length - 1;
    const holdTime = index === 0 ? firstWordDuration : wordDuration;

    timeoutRef.current = setTimeout(() => {
      if (isLast) {
        setReachedEnd(true);
      } else {
        setIndex((i) => i + 1);
      }
    }, holdTime);

    return () => clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // The word sequence can finish before the real assets do — in that case
  // we just hold here on the last word (no timer, no re-render loop) until
  // `ready` flips true, then fire the wipe immediately.
  useEffect(() => {
    if (reachedEnd && ready && !wiping) setWiping(true);
  }, [reachedEnd, ready, wiping]);

  const word = words[index];

  // Panel geometry, all expressed in vh-equivalent viewBox units so the
  // shape's numbers line up exactly with real viewport height:
  //  - corner: where the flat sides sit — pushed curveMarginVh below the
  //    viewport's own bottom edge (100), so the panel still fully covers
  //    the screen through the whole "flat rise" phase.
  //  - control/maxDip: the quadratic control point and the curve's actual
  //    deepest point at its centre (a quadratic bezier with symmetric
  //    endpoints peaks at exactly halfway between the endpoint and control
  //    values, hence control = corner + 2 * depth).
  const corner = 100 + curveMarginVh;
  const maxDip = corner + curveDepthVh;
  const control = corner + curveDepthVh * 2;
  const pathD = `M0,0 H100 V${corner} Q50,${control} 0,${corner} Z`;

  // Phase 1 (flat rise): panel lifts by curveMarginVh with no visible
  // change — its flat corners are still below the viewport the entire
  // time. Phase 2 (curve reveal): continues rising until the curve's
  // deepest point clears the top, i.e. by the panel's full height.
  const marginPercent = `-${curveMarginVh}%`;
  const fullPercent = `-${maxDip}%`;
  const marginFraction = curveMarginVh / maxDip; // where phase 1 ends, as a fraction of total travel

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden pointer-events-none">
      <motion.div
        className="relative w-full h-full"
        initial={{ y: "0%" }}
        animate={
          wiping
            ? {
                y: prefersReducedMotion
                  ? "0%"
                  : ["0%", marginPercent, fullPercent],
              }
            : { y: "0%" }
        }
        transition={
          wiping && !prefersReducedMotion
            ? {
                duration: wipeDuration / 1000,
                times: [0, marginFraction, 1],
                ease: ["easeOut", [0.65, 0, 0.35, 1]],
              }
            : { duration: wipeDuration / 1000 }
        }
        onAnimationComplete={() => {
          if (wiping) onComplete?.();
        }}
        style={
          wiping && prefersReducedMotion
            ? { opacity: 0, transition: `opacity ${wipeDuration}ms ease` }
            : undefined
        }
      >
        {/* curved panel shape — flat corners flush with the viewport's own
            bottom edge (plus the margin buffer), full-bleed with no gaps */}
        <svg
          className="absolute top-0 left-0 w-full"
          style={{ height: `${maxDip}%` }}
          viewBox={`0 0 100 ${maxDip}`}
          preserveAspectRatio="none"
        >
          <path d={pathD} fill={backgroundColor} />
        </svg>

        {/* word display */}
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <motion.span
            key={word}
            initial={index === 0 ? { opacity: 0 } : { opacity: 1 }}
            animate={{ opacity: 1 }}
            transition={{ duration: index === 0 ? 0.45 : 0, ease: "easeOut" }}
            className="font-bold text-center leading-none tracking-tight"
            style={{
              color: textColor,
              fontSize: "clamp(1.75rem, 5vw, 3.25rem)",
            }}
          >
            {word}
          </motion.span>
        </div>
      </motion.div>
    </div>
  );
}
